/**
 * Truncate address to show only up to city name
 * Removes state, pincode, and country from the address string
 *
 * @param {string} fullAddress - The complete address string
 * @param {string} cityName - Optional city name to help identify truncation point
 * @returns {string} Truncated address ending at city name
 *
 * @example
 * truncateAddressToCity("A-35, A Block, Sector 10, Noida, Uttar Pradesh 201301, India")
 * // Returns: "A-35, A Block, Sector 10, Noida"
 */
/**
 * Helper: Check if a part matches the city name
 */
const matchesCity = (part, cityName) => {
	const partLower = part.toLowerCase();
	const cityNameLower = cityName.toLowerCase();
	return (
		partLower === cityNameLower ||
		partLower.includes(cityNameLower) ||
		cityNameLower.includes(partLower)
	);
};

/**
 * Helper: Remove state, pincode, and country from address parts
 */
const removeStatePincodeCountry = (parts) => {
	if (parts.length >= 4) {
		// Remove last 2 parts (state, pincode, country)
		return parts.slice(0, parts.length - 2);
	}
	if (parts.length === 3) {
		// If address has 3 parts, might be "Street, City, State" - keep first 2
		return parts.slice(0, 2);
	}
	return parts;
};

export const truncateAddressToCity = (fullAddress, cityName = '') => {
	if (!fullAddress) return '';

	// Split by comma and trim each part
	const parts = fullAddress.split(',').map((p) => p.trim());

	// If city name is provided, find its position and truncate there
	if (cityName) {
		const cityNameLower = cityName.toLowerCase().trim();
		const cityIndex = parts.findIndex((p) => matchesCity(p, cityNameLower));

		if (cityIndex !== -1) {
			// Return everything up to and including the city
			return parts.slice(0, cityIndex + 1).join(', ');
		}

		// If city not found in address but city name is provided, append it
		// Remove state, pincode, country first, then append city
		const truncatedParts = removeStatePincodeCountry(parts);

		// Check if city is already in truncated parts
		const hasCity = truncatedParts.some((p) => matchesCity(p, cityNameLower));

		// If city is not in the truncated address, append it
		if (!hasCity) {
			return `${truncatedParts.join(', ')}, ${cityName}`;
		}

		return truncatedParts.join(', ');
	}

	// Otherwise, remove last 2-3 parts (typically state, pincode, country)
	const truncatedParts = removeStatePincodeCountry(parts);
	return truncatedParts.join(', ');
};
